// AppShell — Omnix-style top-bar chrome (no persistent rail).
//   • Top AppBar on all sizes: smart Back, logo (→ home), right cluster
//     (Home, WeeklyHours clock, theme toggle, menu drawer with full nav + account).
//   • Home (/) is the modules grid; sections are reached from there or the drawer.
//   • Mobile also keeps a bottom nav for the primary sections.
// Renders chrome only when logged in; otherwise renders children bare (login flow).

import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import LightModeIcon from '@mui/icons-material/LightMode';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { ViewInAr, AddCircle } from '@mui/icons-material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BuildIcon from '@mui/icons-material/Build';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HandymanIcon from '@mui/icons-material/Handyman';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SummarizeIcon from '@mui/icons-material/Summarize';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LockResetIcon from '@mui/icons-material/LockReset';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';

import { useColorMode } from '../theme/ThemeContext';
import logo from '../pages/AIMS-QR.jpg';
import ResetPassword from '../pages/resetPassword';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WeeklyHoursButton from './WeeklyHoursButton';
import DefectAlertsButton from './defects/DefectAlertsButton';
import InstallPrompt from './pwa/InstallPrompt';
import UpdatePrompt from './pwa/UpdatePrompt';
import { baseUrl, userHasModules } from '../shared';
import { hasPendingWork, runSync } from '../offline/sync';
import { requestPersistence, listOfflineVisits } from '../offline/takeOffline';
import useOnlineStatus from '../hooks/useOnlineStatus';
import CloudOffIcon from '@mui/icons-material/CloudOff';

const can = (flag) => localStorage.getItem(flag) === '1';
const clean = (v) => (v ? String(v).replace(/"/g, '') : '');

// Permission-filtered nav (drawer + mobile bottom nav). `primary` → bottom nav.
function buildNav() {
  return [
    // Main section (no header)
    { label: 'Assets', to: '/assets', icon: <ViewInAr />, show: true, primary: true },
    { label: 'Create Asset', to: '/createAsset', icon: <AddCircle />, show: can('canAddAsset') },
    { label: 'Scheduling', to: '/scheduling', icon: <CalendarMonthIcon />, show: can('canViewSchedule') || can('canManageSchedule'), primary: true },
    { label: 'Workshop', to: '/workshop', icon: <HandymanIcon />, show: can('canManageWorkshop'), primary: true },
    { label: 'Inspection', to: '/inspection', icon: <FactCheckIcon />, show: can('canUseDetailedInspection') && !can('canManageWorkshop') },
    { label: 'Defects', to: '/defects', icon: <ReportProblemIcon />, show: can('canAddDefect') || can('canInspectAsset') },
    { label: 'Admin', to: '/admin', icon: <AdminPanelSettingsIcon />, show: can('canRegisterUser') },
    // Reporting section — view/export things for review
    { label: 'Reporting Dashboard', to: '/reporting/platform-dashboard', icon: <AssessmentIcon />, show: can('canViewPlatformDashboard'), group: 'Reporting' },
    { label: 'Schedule Report', to: '/reporting/schedule-report', icon: <EventNoteIcon />, show: can('canManageSchedule'), group: 'Reporting' },
    { label: 'Admin Timesheet Report', to: '/reporting/admin-timesheet-report', icon: <SummarizeIcon />, show: can('canAdminReport'), group: 'Reporting' },
    { label: 'Open Repairs', to: '/openRepairs', icon: <BuildIcon />, show: can('canInspectAsset'), group: 'Reporting' },
    { label: 'Asset Export', to: '/exportfile', icon: <FileDownloadIcon />, show: can('canSearch'), group: 'Reporting' },
    { label: 'Timesheets', to: '/timesheetReview', icon: <ReceiptLongIcon />, show: can('canInspectAsset'), group: 'Reporting' },
  ].filter((i) => i.show);
}

function BrandLogo({ size = 34 }) {
  return (
    <Box component="img" src={logo} alt="A1MS-QR — Asset Inspection Management Solutions" sx={{ height: size, width: size, display: 'block', borderRadius: '8px' }} />
  );
}

function isActive(item, pathname) {
  return pathname === item.to || pathname.startsWith(item.to + '/');
}

function DrawerRow({ item, active, onClick }) {
  const asButton = !item.to && item.onClick;
  const handleClick = (e) => { if (item.onClick) item.onClick(e); if (onClick) onClick(e); };
  return (
    <Box
      component={asButton ? 'button' : Link}
      to={asButton ? undefined : item.to}
      onClick={handleClick}
      sx={{
        ...(asButton ? { border: 'none', background: 'transparent', width: 'calc(100% - 16px)', textAlign: 'left', cursor: 'pointer', font: 'inherit' } : {}),
        display: 'flex', alignItems: 'center', gap: 1.4, textDecoration: 'none',
        px: 1.5, py: 1.1, mx: 1, borderRadius: '10px', fontSize: 14,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--primary)' : 'var(--text)',
        background: active ? 'var(--primary-tint)' : 'transparent',
        '& svg': { fontSize: 20 },
        '&:hover': { background: 'var(--surface-2)' },
      }}
    >
      {item.icon}
      <span>{item.label}</span>
    </Box>
  );
}

function GroupLabel({ children }) {
  return (
    <Box sx={{ px: 2.5, pt: 1.6, pb: 0.5, fontSize: 10, fontWeight: 600, letterSpacing: '.7px', textTransform: 'uppercase', color: 'var(--faint)' }}>
      {children}
    </Box>
  );
}

function NavList({ nav, pathname, onNavigate }) {
  const out = [];
  let groupShown = null;
  nav.forEach((item) => {
    if (item.group && item.group !== groupShown) {
      groupShown = item.group;
      out.push(<GroupLabel key={'g-' + item.group}>{item.group}</GroupLabel>);
    }
    if (!item.group) groupShown = null;
    out.push(<DrawerRow key={item.to || item.label} item={item} active={item.to ? isActive(item, pathname) : false} onClick={onNavigate} />);
  });
  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>{out}</Box>;
}

export default function AppShell({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, toggle } = useColorMode();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const online = useOnlineStatus();
  const [offlineVisitCount, setOfflineVisitCount] = useState(0);
  const [syncClearedNotice, setSyncClearedNotice] = useState(null);

  const loggedIn = localStorage.getItem('username') !== null;
  const canInspectOffline = can('canUseDetailedInspection') || can('canInspectAsset');

  // Track how many visits are cached offline (drives the offline banner + nav).
  useEffect(() => {
    if (!loggedIn) return;
    let cancelled = false;
    listOfflineVisits().then((v) => { if (!cancelled) setOfflineVisitCount(v.length); });
    return () => { cancelled = true; };
  }, [loggedIn, online, location.pathname]);

  // Nav: online = full permission-filtered nav; offline = collapse to just the
  // offline inspection path (the only screen that works with no signal), so we
  // drive the user there instead of to network-dependent screens that break.
  const nav = useMemo(() => {
    if (!loggedIn) return [];
    if (!online) {
      return canInspectOffline
        ? [{ label: 'Offline Inspections', to: '/inspection', icon: <FactCheckIcon />, primary: true }]
        : [];
    }
    return buildNav();
  }, [loggedIn, online, canInspectOffline]);

  // Silent auto-sync (offline inspection): on launch and whenever connectivity
  // returns, drain any queued offline work. Guarded by a cheap pending check so
  // users who never went offline are unaffected; coalesces with the manual
  // "Sync now" button via the engine's in-flight guard.
  useEffect(() => {
    if (!loggedIn) return undefined;
    let cancelled = false;
    const trigger = async () => {
      if (cancelled || !navigator.onLine) return;
      try {
        if (await hasPendingWork()) {
          const summary = await runSync();
          // The only auto-sync outcome worth interrupting for: this device's
          // offline checkout was cleared while it was away (HQ force-clear or
          // a teammate released the visit). Its work has been uploaded and
          // the local copy removed; tell the user so it isn't a surprise.
          if (!cancelled && summary && summary.clearedVisits && summary.clearedVisits.length) {
            setSyncClearedNotice(summary.clearedVisits
              .map((v) => v.visitDeleted
                ? `Visit #${v.visitId} was deleted at HQ. Your completed inspections were uploaded and the offline copy was removed.`
                : `Your offline checkout of visit #${v.visitId} was cleared${v.clearedBy ? ' by ' + v.clearedBy : ''}. Your completed inspections were uploaded and the offline copy was removed.`)
              .join(' '));
          }
        }
      } catch (_) { /* silent */ }
    };
    // If there's offline data, ask the browser to resist evicting it (iOS §7).
    // Independent of connectivity, so runs regardless of navigator.onLine.
    (async () => {
      try { if (await hasPendingWork()) await requestPersistence(); } catch (_) { /* silent */ }
    })();
    trigger();
    window.addEventListener('online', trigger);
    return () => { cancelled = true; window.removeEventListener('online', trigger); };
  }, [loggedIn]);

  if (!loggedIn) return <>{children}</>;

  const name = clean(localStorage.getItem('name'));
  const company = clean(localStorage.getItem('company'));
  const isHome = location.pathname === '/';
  // No-module users live permanently on /assets (Home redirects there), so Home and
  // the Assets nav item are dead-ends/redundant for them — hide both.
  const hasModules = userHasModules();

  // Smart back: real history when navigated in-app, else a parent-map fallback.
  const handleBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) { navigate(-1); return; }
    const p = location.pathname;
    if (p.startsWith('/workshop/')) { navigate('/workshop'); return; }
    navigate('/');
  };

  const accountActions = [
    { label: 'Reset Password', icon: <LockResetIcon />, onClick: () => { setResetOpen(true); setDrawerOpen(false); } },
    { label: 'Logout', icon: <MeetingRoomIcon />, onClick: () => { setLogoutOpen(true); setDrawerOpen(false); } },
  ];

  const doLogout = () => {
    setLoggingOut(true);
    fetch(baseUrl + 'user/logout', {
      method: 'POST',
      headers: { username: localStorage.getItem('username'), bToken: localStorage.getItem('bToken') },
    })
      .then((r) => r.json())
      .catch(() => {})
      .finally(() => { localStorage.clear(); window.location.href = '/'; });
  };

  // No-module users live on /assets, so drop the redundant Assets primary — their
  // bottom nav becomes just "More" (→ Account / logout).
  const primaries = nav.filter((i) => i.primary && (hasModules || i.to !== '/assets'));
  const bottomValue = primaries.find((i) => isActive(i, location.pathname))?.to || false;
  const handleBottom = (_e, val) => {
    if (val === '__more') setDrawerOpen(true);
    else navigate(val);
  };

  const dialogs = (
    <>
      <Snackbar
        open={Boolean(syncClearedNotice)}
        onClose={() => setSyncClearedNotice(null)}
        autoHideDuration={12000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setSyncClearedNotice(null)} sx={{ maxWidth: 480 }}>
          {syncClearedNotice}
        </Alert>
      </Snackbar>
      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Log out?</DialogTitle>
        <DialogContent><DialogContentText>You'll be signed out of AIMS and returned to the login screen.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutOpen(false)} disabled={loggingOut}>Cancel</Button>
          <Button onClick={doLogout} variant="contained" color="error" disabled={loggingOut}>{loggingOut ? 'Logging out…' : 'Log out'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent><ResetPassword /></DialogContent>
        <DialogActions><Button onClick={() => setResetOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <AppBar position="sticky" component="div" elevation={0} sx={{ background: 'var(--rail)', color: 'var(--on-rail)' }}>
        <Toolbar sx={{ gap: 0.5 }}>
          {!isHome && (
            <Button onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ color: 'var(--on-rail)', minWidth: 0, mr: 0.5 }}>
              {isMobile ? '' : 'Back'}
            </Button>
          )}
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', color: 'inherit', flexGrow: 1, minWidth: 0 }}>
            <BrandLogo size={42} />
            {!isMobile && <Box sx={{ fontWeight: 700, fontSize: 17, letterSpacing: '.02em', whiteSpace: 'nowrap' }}>1 Maintenance & Supply</Box>}
          </Box>
          {!isHome && hasModules && (
            <Tooltip title="Home">
              <IconButton component={Link} to="/" sx={{ color: 'var(--on-rail)' }}><HomeOutlinedIcon /></IconButton>
            </Tooltip>
          )}
          {!online && (
            <Tooltip title="Offline">
              <Box
                role="status"
                aria-label="Offline"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  gap: isMobile ? 0 : 0.75,
                  height: 32,
                  width: isMobile ? 32 : 'auto',
                  px: isMobile ? 0 : 1.25,
                  borderRadius: isMobile ? '50%' : 'var(--r-pill)',
                  bgcolor: 'var(--warn-bg)',
                  color: 'var(--warn)',
                  border: '1px solid var(--warn)',
                  fontWeight: 700,
                  fontSize: 13,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                <CloudOffIcon sx={{ fontSize: 18 }} />
                {!isMobile && 'Offline'}
              </Box>
            </Tooltip>
          )}
          {online && (can('canAddDefect') || can('canInspectAsset')) && <DefectAlertsButton />}
          {online && can('canInspectAsset') && <WeeklyHoursButton />}
          <Tooltip title={mode === 'light' ? 'Dark mode' : 'Light mode'}>
            <IconButton onClick={toggle} sx={{ color: 'var(--on-rail)' }}>{mode === 'light' ? <BedtimeIcon /> : <LightModeIcon />}</IconButton>
          </Tooltip>
          {!isMobile && (
            <Tooltip title="Menu">
              <IconButton edge="end" onClick={() => setDrawerOpen(true)} sx={{ color: 'var(--on-rail)' }}><MenuIcon /></IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>

      {/* Offline quick-path banner: when offline, most screens can't load, so give
          a clear cue + a one-tap route to the offline inspection visits. */}
      {!online && canInspectOffline && !location.pathname.startsWith('/inspection') && (
        <Box
          onClick={() => navigate('/inspection')}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
            px: { xs: 2, md: 3 }, py: 1.25,
            background: 'var(--warn-tint, rgba(245,166,35,0.14))',
            borderBottom: '1px solid var(--warn, rgba(245,166,35,0.4))',
            color: 'var(--warn, #92610a)',
          }}
        >
          <CloudOffIcon fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: 13, fontWeight: 700 }}>You’re offline</Box>
            <Box sx={{ fontSize: 12 }}>
              {offlineVisitCount > 0
                ? `${offlineVisitCount} visit${offlineVisitCount === 1 ? '' : 's'} ready to inspect. Tap to open.`
                : 'Only offline inspections are available. Tap to open.'}
            </Box>
          </Box>
          <Button size="small" variant="contained" color="warning" sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 700 }}>
            Open
          </Button>
        </Box>
      )}

      <Box component="main" sx={{ p: { xs: 2, md: 3 }, pb: { xs: '84px', sm: 3 } }}>
        {children}
      </Box>

      {/* PWA install prompt - self-gates to inspectors only (see InstallPrompt). */}
      <InstallPrompt />

      {/* New-version prompt - never auto-reloads; guards mid-form / unsynced work. */}
      <UpdatePrompt />

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={isMobile ? { sx: { borderTopLeftRadius: 'var(--r)', borderTopRightRadius: 'var(--r)', maxHeight: '75vh' } } : undefined}
      >
        <Box sx={{ width: isMobile ? 'auto' : 268, py: 2, pb: isMobile ? '84px' : 2, background: 'var(--surface)', height: isMobile ? 'auto' : '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2, pb: 1.5, mb: 0.5, borderBottom: '1px solid var(--border)' }}>
            <Box sx={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{name || 'Signed in'}</Box>
            {company && <Box sx={{ fontSize: 11, color: 'var(--muted)' }}>{company}</Box>}
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <NavList
              nav={[
                ...(hasModules ? [{ label: 'Home', to: '/', icon: <HomeOutlinedIcon /> }] : []),
                // For no-module users /assets IS home — drop the redundant Assets row.
                ...nav.filter((i) => hasModules || i.to !== '/assets'),
              ]}
              pathname={location.pathname}
              onNavigate={() => setDrawerOpen(false)}
            />
            <GroupLabel>Account</GroupLabel>
            <NavList nav={accountActions} pathname={location.pathname} onNavigate={() => setDrawerOpen(false)} />
          </Box>
        </Box>
      </Drawer>

      {isMobile && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100, borderTop: '1px solid var(--border)' }} elevation={3}>
          <BottomNavigation value={bottomValue} onChange={handleBottom} showLabels>
            {primaries.map((i) => (
              <BottomNavigationAction key={i.to} label={i.label} value={i.to} icon={i.icon} />
            ))}
            <BottomNavigationAction label="More" value="__more" icon={<MoreHorizIcon />} />
          </BottomNavigation>
        </Paper>
      )}

      {dialogs}
    </Box>
  );
}
